import { AppShell } from '@mantine/core';
import { ReactNode, useContext } from 'react';
import { GlobalContext } from '../../contexts';
import { FloraAside } from '../flora/flora-aside';
import { useFlora } from '../flora/flora.context';
import { FLORA_ASIDE_RIGHT_GAP } from '../flora/flora.type';
import { GlobalMessageComponent } from '../global-message/global-message';
import { HeaderBar } from '../header/header';
import { SideBar } from '../side-bar/side-bar';
import { SidebarContext } from '../side-bar/side-bar.context';
type MenuDrivenContainerProps = {
  children: ReactNode;
  header?: string | ReactNode;
  actions?: ReactNode;
  hideFloraAside?: boolean;
};

export const MenuDrivenContainer = ({
  children,
  header,
  actions,
  hideFloraAside = false,
}: MenuDrivenContainerProps) => {
  const { sidebarOpen } = useContext(SidebarContext);
  const { globalMessage } = useContext(GlobalContext);
  const { isFloraVisible, toggleFlora, floraWidth, isResizingFlora } = useFlora();

  const navbarWidth = sidebarOpen ? 280 : 70;
  const globalMessageHeight = globalMessage ? 50 : 0; // GlobalMessageComponent has height of 50px
  // Whether the Flora aside is reserving layout space right now.
  const floraAsideReserved = isFloraVisible && !hideFloraAside;

  return (
    <>
      {globalMessage && <GlobalMessageComponent globalMessage={globalMessage} />}
      <AppShell
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        layout="alt"
        header={{ height: 52 }}
        navbar={{
          width: navbarWidth,
          breakpoint: 'never',
          collapsed: { mobile: false, desktop: false },
        }}
        aside={{
          width: floraAsideReserved ? floraWidth + FLORA_ASIDE_RIGHT_GAP : 0,
          breakpoint: 'never',
        }}
        padding={16}
        withBorder={false}
        styles={{
          header: {
            top: globalMessageHeight,
            transition: 'top 0.3s ease',
          },
          navbar: {
            top: globalMessageHeight,
            transition: 'top 0.3s ease',
          },
          main: {
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            // Reserve the aside via margin (not Mantine's default padding) so the
            // main clip box ends at the aside's left edge. Mantine pads a
            // full-width main, which would let overflowing content paint UNDER the
            // fixed aside; margin keeps the box — and thus overflow-x scrolling —
            // clear of it. Mirrors Mantine's own layout="alt" Header reservation.
            // Logical props match Mantine's longhands so the inline override wins.
            ...(floraAsideReserved
              ? {
                  overflowX: 'auto' as const,
                  paddingInlineEnd: 'var(--app-shell-padding)',
                  marginInlineEnd: floraWidth + FLORA_ASIDE_RIGHT_GAP,
                }
              : {}),
            // Skip the reflow animation while actively dragging the resize handle
            // so the main content tracks the pointer instead of lagging behind.
            transition: isResizingFlora ? 'none' : 'margin-inline-end 0.3s ease, padding-inline-end 0.3s ease',
            height: 'calc(100vh - 52px)',
            marginTop: globalMessageHeight,
          },
          aside: {
            transition: 'transform 0.3s ease',
            marginTop: 24 + globalMessageHeight,
            height: 'calc(100vh - 42px)',
            // Keep the rendered gap in lockstep with the reserved layout slot.
            marginRight: FLORA_ASIDE_RIGHT_GAP,
            background: 'transparent',
            border: 'none',
          },
        }}
      >
        <HeaderBar header={header} actions={actions} hideFloraAside={hideFloraAside} />
        <SideBar />
        {hideFloraAside
          ? null
          : isFloraVisible && (
              <AppShell.Aside w={floraWidth} style={{ zIndex: 500 }}>
                <FloraAside onClose={toggleFlora} />
              </AppShell.Aside>
            )}
        <AppShell.Main>{children}</AppShell.Main>
      </AppShell>
    </>
  );
};
